import {useState} from 'react';
import {
	createStyles,
	Header,
	Container,
	Group,
	Title,
	Menu,
	Avatar,
	Text,
	UnstyledButton, Divider
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
	IconLogout,
	IconChevronDown,
	IconMoon, IconSearch,
	IconNotes,
	IconBell,
	IconArrowLeft
} from '@tabler/icons';
import {NavLink, useNavigate, useLocation} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../utils/hooks";
import {themeColorSchemeSwitch} from "../../store/reducers/general";
import {auth} from "../../api/api-firebase";
import {signOut} from "firebase/auth";
import { Spacer } from "../atoms/Spacer"
import { appVersion } from "../../utils/constants"
import withReactContent from "sweetalert2-react-content"
import Swal from "sweetalert2"

const HEADER_HEIGHT = 60;

const useStyles = createStyles((theme) => ({
	root: {
		position: 'fixed',
		zIndex: 100,
		top: 0,
		left: 0,
		right: 0,

		[theme.fn.smallerThan('sm')]: {
			marginBottom: 30,
		},
	},

	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		height: '100%',
	},

	headerSection: {
		[theme.fn.smallerThan('sm')]: {
			flex: 1,
			display: 'flex',
			alignItems: 'center',
		},
	},

	headerSectionCenter: {
		[theme.fn.smallerThan('sm')]: {
			flex: 1,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
	},

	headerSectionRight: {
		[theme.fn.smallerThan('sm')]: {
			flex: 1,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-end',
		},
	},

	logo: {
		textDecoration: 'none',
		color: theme.colors.primaryColor,
		'&, &:hover': {
			textDecoration: 'none',
		},
	},

	links: {
		[theme.fn.smallerThan('sm')]: {
			display: 'none',
		},
	},

	logout: {
		position: 'relative',
		top: 2,
		[theme.fn.largerThan('sm')]: {
			display: 'none',
		},
		[theme.fn.smallerThan('sm')]: {
			marginLeft: 16,
		},
	},

	link: {
		display: 'block',
		lineHeight: 1,
		padding: '8px 12px',
		borderRadius: theme.radius.sm,
		textDecoration: 'none',
		color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
		fontSize: theme.fontSizes.sm,
		fontWeight: 500,
		cursor: 'pointer',

		[theme.fn.smallerThan('sm')]: {
			borderRadius: 0,
			padding: theme.spacing.md,
			marginLeft: 10,
		},

		[theme.fn.largerThan('sm')]: {
			'&:hover': {
				backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
			},
		},
	},

	linkWatchlist: {
		display: "none",
		[theme.fn.smallerThan('sm')]: {
			display: "none",
			paddingLeft: 0,
			paddingRight: 0,
		},
	},

	linkSearch: {
		[theme.fn.smallerThan('sm')]: {
			display: "none",
			paddingLeft: 0,
			paddingRight: 0 + ' !important',
		},
	},

	linkNotification: {
		position: 'relative',
		marginLeft: 10,
		[theme.fn.smallerThan('sm')]: {
			paddingLeft: 0,
			paddingRight: 0 + ' !important',
		},
	},

	notificationBubble: {
		position: 'absolute',
		borderRadius: '50%',
		backgroundColor: theme.colors.red,
		width: 7,
		height: 7
	},

	backButton: {
		[theme.fn.largerThan('sm')]: {
			display: 'none',
		},
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		paddingRight: theme.spacing.sm,
	},

	linkActive: {
		'&, &:hover': {
			backgroundColor: theme.fn.variant({variant: 'light', color: theme.primaryColor}).background,
			color: theme.fn.variant({variant: 'light', color: theme.primaryColor}).color,
		},
	},

	user: {
		color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
		padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
		borderRadius: theme.radius.sm,
		transition: 'background-color 100ms ease',

		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
		},

		[theme.fn.smallerThan('xs')]: {
			display: 'none',
		},
	},

	userActive: {
		backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
	},

	tabs: {
		[theme.fn.smallerThan('sm')]: {
			display: 'none',
		},
	},

	tabsList: {
		borderBottom: '0 !important',
	},


}));

interface HeaderProps {
	links: { link: string; label: string }[];
}

export function Navbar({links}: HeaderProps) {

	const userState = useAppSelector((state) => state.user)
	const generalSettings = useAppSelector((state) => state.general)
	const newNotifications = useAppSelector((state) => state.notifications.newNotifications)

	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const location = useLocation()
	const {classes, cx} = useStyles();
	const [userMenuOpened, setUserMenuOpened] = useState(false);

	const rSwal = withReactContent(Swal)

	const largeScreen = useMediaQuery('(min-width: 768px)');

	const hideBackButton = ['/', '/watchlist', '/friends', '/friends/search', '/recently-viewed', '/movie/search'].includes(location.pathname)

	const items = links.map((link) => (
		<NavLink
			key={link.label}
			to={link.link}
			className={({isActive}) => classes.link + ' ' + (isActive ? classes.linkActive : '')}
		>
			{link.label}
		</NavLink>
	));

	return (
		<>
			<Header height={HEADER_HEIGHT} className={classes.root}>
				<Container className={classes.header}>

					{largeScreen ? (
						<>
							<Title order={4}>
								<Group>
									<Text
										component={NavLink}
										variant="gradient"
										gradient={{ from: 'red', to: 'orange', deg: 45 }}
										to="/"
										className={classes.logo}
									>
										Moviebook
									</Text>
									<Text
										size="xs"
										color="dimmed"
									>
										{appVersion}
									</Text>
								</Group>
							</Title>

							<Group spacing={0}>

								<Group spacing={5} className={classes.links}>
									{items}
									{
										userState.isLogged &&
											<Menu
												width={260}
												position="bottom-end"
												transition="pop-top-right"
												onClose={() => setUserMenuOpened(false)}
												onOpen={() => setUserMenuOpened(true)}
											>
												<Menu.Target>
													<UnstyledButton
														className={cx(classes.user, { [classes.userActive]: userMenuOpened })}
													>
														<Group spacing={7}>
															<Avatar color="white" src={userState.data.avatar} alt={userState.data.name} radius="xl" size={20} />
															<Text weight={500} size="sm" sx={{ lineHeight: 1 }} mr={3}>
																{userState.data.name}
															</Text>
															<IconChevronDown size={12} stroke={1.5} />
														</Group>
													</UnstyledButton>
												</Menu.Target>
												<Menu.Dropdown>
													<Menu.Item
														icon={<IconMoon size={14} stroke={1.5} />}
														onClick={() => dispatch(themeColorSchemeSwitch())}
													>
														{
															(generalSettings.themeSettings.colorScheme === 'light') ? 'Dark mode' : 'Light mode'
														}
													</Menu.Item>
													{/*<Menu.Item icon={<IconSettings size={14} stroke={1.5} />}>Impostazioni</Menu.Item>*/}
													<Menu.Item
														icon={<IconLogout size={14} stroke={1.5} />}
														onClick={async () => {
															await signOut(auth)
															navigate('/user/signin')
														}}>Logout
													</Menu.Item>
												</Menu.Dropdown>
											</Menu>
									}
								</Group>

								<Divider my={10} orientation="vertical" size="xs" style={{display: largeScreen ? "flex" : "none"}} />

								<Text
									component={NavLink}
									to="/watchlist"
									className={classes.link + ' ' + classes.linkWatchlist}
								>
									<IconNotes stroke={1.5} />
								</Text>

								<Text
									component={NavLink}
									to="/notifications"
									className={classes.link + ' ' + classes.linkNotification}
								>
									<IconBell stroke={1.5} />
									{
										newNotifications && <span className={classes.notificationBubble} />
									}
								</Text>

								<Text
									component={NavLink}
									to="/movie/search"
									className={classes.link + ' ' + classes.linkSearch}
								>
									<IconSearch stroke={1.5} />
								</Text>

								<Text
									className={classes.logout}
									onClick={() => {
										rSwal.fire({
											title: 'Sei sicuro?',
											text: "Stai per effettuare il logout",
											icon: 'warning',
											showCancelButton: true,
											confirmButtonColor: '#3085d6',
											cancelButtonColor: '#d33',
											confirmButtonText: 'Logout',
											cancelButtonText: 'Annulla',
										}).then(async ({isConfirmed}) => {
											if (isConfirmed) {
												await signOut(auth)
												navigate('/user/signin')
											}
										})
									}}
								>
									<IconLogout stroke={1.5} />
								</Text>

							</Group>
						</>
					) : (
						<>
							{!hideBackButton && (
								<div className={classes.headerSection}>
									<Text
										className={classes.backButton}
										onClick={() => navigate(-1)}
									>
										<IconArrowLeft stroke={1.5} />
									</Text>
								</div>
							)}
							{hideBackButton && <div className={classes.headerSection} />}

							<div className={classes.headerSectionCenter}>
								<Title order={4}>
									<Group>
										<Text
											component={NavLink}
											variant="gradient"
											gradient={{ from: 'red', to: 'orange', deg: 45 }}
											to="/"
											className={classes.logo}
										>
											Moviebook
										</Text>
									</Group>
								</Title>
							</div>

							<div className={classes.headerSectionRight}>
								<Group spacing={0}>

									<Group spacing={5} className={classes.links}>
										{items}
										{
											userState.isLogged &&
												<Menu
													width={260}
													position="bottom-end"
													transition="pop-top-right"
													onClose={() => setUserMenuOpened(false)}
													onOpen={() => setUserMenuOpened(true)}
												>
													<Menu.Target>
														<UnstyledButton
															className={cx(classes.user, { [classes.userActive]: userMenuOpened })}
														>
															<Group spacing={7}>
																<Avatar color="white" src={userState.data.avatar} alt={userState.data.name} radius="xl" size={20} />
																<Text weight={500} size="sm" sx={{ lineHeight: 1 }} mr={3}>
																	{userState.data.name}
																</Text>
																<IconChevronDown size={12} stroke={1.5} />
															</Group>
														</UnstyledButton>
													</Menu.Target>
													<Menu.Dropdown>
														<Menu.Item
															icon={<IconMoon size={14} stroke={1.5} />}
															onClick={() => dispatch(themeColorSchemeSwitch())}
														>
															{
																(generalSettings.themeSettings.colorScheme === 'light') ? 'Dark mode' : 'Light mode'
															}
														</Menu.Item>
														{/*<Menu.Item icon={<IconSettings size={14} stroke={1.5} />}>Impostazioni</Menu.Item>*/}
														<Menu.Item
															icon={<IconLogout size={14} stroke={1.5} />}
															onClick={async () => {
																await signOut(auth)
																navigate('/user/signin')
															}}>Logout
														</Menu.Item>
													</Menu.Dropdown>
												</Menu>
										}
									</Group>

									<Divider my={10} orientation="vertical" size="xs" style={{display: largeScreen ? "flex" : "none"}} />

									<Text
										component={NavLink}
										to="/watchlist"
										className={classes.link + ' ' + classes.linkWatchlist}
									>
										<IconNotes stroke={1.5} />
									</Text>

									<Text
										component={NavLink}
										to="/notifications"
										className={classes.link + ' ' + classes.linkNotification}
									>
										<IconBell stroke={1.5} />
										{
											newNotifications && <span className={classes.notificationBubble} />
										}
									</Text>

									<Text
										component={NavLink}
										to="/movie/search"
										className={classes.link + ' ' + classes.linkSearch}
									>
										<IconSearch stroke={1.5} />
									</Text>

									<Text
										className={classes.logout}
										onClick={() => {
											rSwal.fire({
												title: 'Sei sicuro?',
												text: "Stai per effettuare il logout",
												icon: 'warning',
												showCancelButton: true,
												confirmButtonColor: '#3085d6',
												cancelButtonColor: '#d33',
												confirmButtonText: 'Logout',
												cancelButtonText: 'Annulla',
											}).then(async ({isConfirmed}) => {
												if (isConfirmed) {
													await signOut(auth)
													navigate('/user/signin')
												}
											})
										}}
									>
										<IconLogout stroke={1.5} />
									</Text>

								</Group>
							</div>
						</>
					)}

				</Container>
			</Header>
			<Spacer smallerXs={120} largerXs={80} />
		</>
	);
}
